import { useTranslation } from 'react-i18next';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';

import {
  InternalPage,
  Spacer,
  addSnackbar,
  HomeLink,
} from 'theSource/components';
import {
  useBenefitsQuery,
  BenefitDto,
  useConfigurationQuery,
} from 'theSource/queries';
import { api, queryClient } from 'theSource/utils';
import { PsButtonTuner, PsSelectTuner } from 'stencilSetup';
import { BeneficiariesSection } from './components';
import { filteredBeneficiaries, StateBeneficiaryDto } from './utils';
import {
  setAllBeneficiaryData,
  selectNewBeneficiary,
} from 'enrollment/features/AddYourBeneficiaries/services';
import { BeneficiariesDto } from 'enrollment/queries/useEnrollmentAddBeneficiaryQuery/types';
import { contextTypes } from 'features/MyBenefits/utils';
import { AlertBox } from 'components';
import { ThemedHeader } from 'components/ThemedHeader';

export const MyBeneficiaries = () => {
  const { t } = useTranslation('pages.myBeneficiaries');
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [selectedContext, setSelectedContext] = useState<string>('Current');
  const [benefitsToDisplay, setBenefitsToDisplay] = useState<BenefitDto[]>([]);
  const [disable, setDisabled] = useState(true);
  const stateBeneficiaries = useSelector(selectNewBeneficiary);

  const { configuration } = useConfigurationQuery();
  const lumosApiHost = configuration?.apiHost;

  const {
    data: benefitsData,
    isLoading: isLoadingBenefits,
    isFetching: isFetchingBenefits,
  } = useBenefitsQuery({ enabled: true });

  useEffect(() => {
    if (!isLoadingBenefits && !isFetchingBenefits && benefitsData) {
      const pastBenefits = benefitsData.data.previous?.filter(
        (benefit) => benefit.coverage.beneficiaries?.length > 0
      );
      const currentBenefits = benefitsData.data.current?.filter(
        (benefit) => benefit.coverage.beneficiaries?.length > 0
      );
      const futureBenefits = benefitsData.data.next?.filter(
        (benefit) => benefit.coverage.beneficiaries?.length > 0
      );

      let benefitsToDisplay;
      switch (selectedContext.toLowerCase()) {
        case 'past':
          benefitsToDisplay = pastBenefits;
          break;
        case 'future':
          benefitsToDisplay = futureBenefits;
          break;
        case 'current':
        default:
          benefitsToDisplay = currentBenefits;
          break;
      }
      setBenefitsToDisplay(benefitsToDisplay);
      updateBeneficiaries(benefitsToDisplay);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedContext, benefitsData, isLoadingBenefits, isFetchingBenefits]);

  const updateBeneficiaries = (data: BenefitDto[] | undefined) => {
    const initialPlansState = filteredBeneficiaries(data)?.filter(
      (beneficiaryData) =>
        beneficiaryData !== null && beneficiaryData !== undefined
    );
    initialPlansState?.length &&
      dispatch(setAllBeneficiaryData(initialPlansState));
  };

  const handleContextSelect = (e: any) => {
    const context = e.target.value;
    setSelectedContext(context);
  };

  const handleCancel = (e: React.SyntheticEvent) => {
    e.preventDefault();
    navigate(-1);
  };

  const saveBeneficiaries = ({
    orgBenefitId,
    beneficiaries,
  }: {
    orgBenefitId: number;
    beneficiaries: BeneficiariesDto[];
  }) => {
    api
      .put(
        `${lumosApiHost}/v2/self_service/beneficiaries/coverages/${orgBenefitId}/?cart_mode=false`,
        {
          beneficiaries: beneficiaries,
        }
      )
      .then(() => {
        dispatch(
          addSnackbar({
            message: t('save_successful'),
            severity: 'success',
          })
        );
        queryClient.refetchQueries('benefits');
      })
      .catch(() => {
        dispatch(
          addSnackbar({
            message: t('save_successful'),
            severity: 'error',
          })
        );
      });
  };

  const saveAllBeneficiariesForPlanYear = () => {
    const beneficiaryData = stateBeneficiaries as StateBeneficiaryDto;
    const benefits = beneficiaryData?.beneficiaryData;
    benefits.forEach((benefit) => {
      const beneficiaries = benefit.beneficiaries;
      saveBeneficiaries({
        orgBenefitId: benefit.orgBenefitId,
        beneficiaries: beneficiaries,
      });
    });
  };

  const handleSave = (e: React.SyntheticEvent) => {
    e.preventDefault();
    saveAllBeneficiariesForPlanYear();
  };

  const planYearStart = new Date(
    benefitsToDisplay[0]?.coverage?.plan_year_start_date
  ).toLocaleDateString('en-US', {
    month: '2-digit',
    day: '2-digit',
    year: 'numeric',
  });
  const planYearEnd = new Date(
    benefitsToDisplay[0]?.coverage?.plan_year_end_date
  ).toLocaleDateString('en-US', {
    month: '2-digit',
    day: '2-digit',
    year: 'numeric',
  });
  const planYearDates =
    planYearStart && planYearEnd ? `${planYearStart} - ${planYearEnd}` : '';

  return (
    <InternalPage
      testId="my-beneficiaries-page"
      CustomActionComponent={<HomeLink />}
      customActionAlignment="left"
    >
      <Spacer height={4} />
      <ThemedHeader cssClass="mt-n1">{t('title')}</ThemedHeader>
      <p>{t('description')}</p>
      <Spacer height={3} />
      <div className="col col-md-6 pl-0 pb-4">
        <PsSelectTuner
          inputId="context-select"
          data-testid="context-select"
          label="Choose the Plan Year"
          value={selectedContext}
          onPsChange={(e) => handleContextSelect(e.detail.event as any)}
        >
          {contextTypes?.map((context, index) => (
            <option key={index} value={context}>
              {t('your')} {context} {t('beneficiaries')}
            </option>
          ))}
        </PsSelectTuner>
      </div>
      {benefitsToDisplay.length > 0 ? (
        <>
          <h3 className="mb-3">
            {selectedContext} Plan Year: {planYearDates}
          </h3>
          <BeneficiariesSection
            benefits={benefitsToDisplay}
            setDisabled={setDisabled}
          />
          <div className="d-flex justify-content-end">
            <PsButtonTuner
              buttonId="cancel-beneficiaries-button"
              data-testid="cancel-beneficiaries-button"
              label={t('common:cancel')}
              level="tertiary"
              onClick={handleCancel}
              extraClass="text-nowrap mr-3"
            />
            <PsButtonTuner
              buttonId="save-beneficiaries-button"
              data-testid="save-beneficiaries-button"
              label={t('common:save')}
              level="primary"
              onClick={handleSave}
              disabled={disable}
              extraClass="text-nowrap"
            />
          </div>
        </>
      ) : (
        <AlertBox innerText={t('no_beneficiaries')} />
      )}
    </InternalPage>
  );
};
